<template>
  <div class="support-center-page">
    <div class="container">
      <header class="support-header">
        <span class="support-kicker">{{ $t('ticketCenter.kicker') }}</span>
        <h1>{{ $t('ticketCenter.title') }}</h1>
        <p>{{ $t('ticketCenter.centerSubtitle') }}</p>
      </header>

      <nav class="support-tabs" role="tablist" :aria-label="$t('ticketCenter.tabsLabel')">
        <button
          v-for="tab in tabs"
          :id="`support-${tab.value}-tab`"
          :key="tab.value"
          type="button"
          role="tab"
          class="support-tab"
          :class="{ active: activeTab === tab.value }"
          :aria-selected="activeTab === tab.value"
          :aria-controls="`support-${tab.value}-panel`"
          @click="selectTab(tab.value)"
        >
          {{ tab.label }}
        </button>
      </nav>
    </div>

    <section
      v-if="activeTab === 'tickets'"
      id="support-tickets-panel"
      role="tabpanel"
      aria-labelledby="support-tickets-tab"
    >
      <TicketCenter embedded />
    </section>
    <section
      v-else
      id="support-messages-panel"
      role="tabpanel"
      aria-labelledby="support-messages-tab"
    >
      <MessageProcessing embedded />
    </section>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import TicketCenter from '@/views/TicketCenter.vue'
import MessageProcessing from '@/views/MessageProcessing.vue'

export default {
  name: 'SupportCenterView',
  components: {
    TicketCenter,
    MessageProcessing
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const { t } = useI18n()
    const activeTab = computed(() => (
      route.query.tab === 'messages' && route.query.create !== 'true' ? 'messages' : 'tickets'
    ))
    const tabs = computed(() => [
      { value: 'tickets', label: t('ticketCenter.ticketsTab') },
      { value: 'messages', label: t('ticketCenter.messagesTab') }
    ])

    const selectTab = (tab) => {
      if (activeTab.value === tab) return
      router.replace({
        path: '/tickets',
        query: tab === 'messages' ? { tab: 'messages' } : {}
      })
    }

    return { activeTab, tabs, selectTab }
  }
}
</script>

<style scoped>
.support-center-page {
  min-height: calc(100vh - var(--header-height) - var(--footer-height));
  padding-top: 44px;
  background: var(--background-color);
}

.support-header {
  max-width: 760px;
}

.support-kicker {
  display: inline-block;
  margin-bottom: 8px;
  color: var(--primary-color);
  font-size: 13px;
  font-weight: 700;
}

.support-header h1 {
  margin: 0 0 10px;
  color: var(--text-primary);
  font-size: 36px;
  line-height: 1.2;
  letter-spacing: 0;
}

.support-header p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 16px;
  line-height: 1.7;
}

.support-tabs {
  display: flex;
  gap: 4px;
  margin-top: 28px;
  border-bottom: 1px solid var(--border-color);
}

.support-tab {
  position: relative;
  min-width: 112px;
  min-height: 48px;
  padding: 0 20px;
  border: 0;
  background: transparent;
  color: var(--text-secondary);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s, background-color 0.2s;
}

.support-tab:hover {
  background: var(--hover-color);
  color: var(--primary-color);
}

.support-tab:focus-visible {
  outline: 3px solid rgba(68, 97, 242, 0.24);
  outline-offset: -3px;
}

.support-tab.active {
  color: var(--primary-color);
}

.support-tab.active::after {
  position: absolute;
  right: 12px;
  bottom: -1px;
  left: 12px;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: var(--primary-color);
  content: '';
}

@media (max-width: 640px) {
  .support-center-page {
    padding-top: 28px;
  }

  .support-header h1 {
    font-size: 30px;
  }

  .support-header p {
    font-size: 15px;
  }

  .support-tabs {
    margin-top: 20px;
  }

  .support-tab {
    flex: 1;
    min-width: 0;
    padding: 0 12px;
  }
}
</style>
